import { Card } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { CompetencyProfileCard } from '../CompetencyProfile/CompetencyProfileCard';
import { Evaluation360Panel, ManagerAssessmentPanel, SelfEvaluationPanel } from '../Evaluations';
import { HEADLINE_CARD_SURFACE, HEADLINE_CARD_TITLE } from './headlineCard';
import { PerformanceScoreDial } from '../PerformanceScoreDial';
import { ObjectivesAchievementCard } from './ObjectivesAchievementCard';
import { PerformanceTrendChart } from './PerformanceTrend';
import { SkillsetAssessmentCard } from '../SkillsetAssessment/SkillsetAssessmentCard';

import type { SkillsetFilter } from '../../hooks/useProfileScore';
import type { PerformanceScore } from '@/types/performance-profile';

import { useGetUserById } from '@/api/directory';

interface AssessmentTabProps {
    scoreData: PerformanceScore;
    employeeId: string;
    onEdit: () => void;
    canStartEvaluations?: boolean;
    /**
     * `canAssignCompetencyProfiles`: decides whether the profile card offers to assign one. It is
     * the policy the server enforces on the assignment endpoint, and the page is its one source.
     */
    canAssignCompetencyProfiles?: boolean;
    /** The profile's period lens, shared with the Objectives tab. */
    pickedPeriodId: string | null;
    onPickPeriod: (periodId: string) => void;
    onViewAllObjectives: () => void;
    /** False while the Objectives feature is off: the objectives card then has nothing to read. */
    showsObjectives: boolean;
    /** The competency lens the page holds; the score card above reads the same evaluations. */
    skillsetFilter: SkillsetFilter;
    onSkillsetFilterChange: (filter: SkillsetFilter) => void;
    /** The period the score covers, named the same way on the Croissance tab. */
    scorePeriodLabel: string | null;
    /**
     * True while the score on screen still answers the lens the reader just left. The card says so
     * rather than emptying itself: a card that disappears takes the page's height with it, and the
     * reader is thrown back to the top for the length of a request.
     */
    isScoreStale?: boolean;
}

/**
 * The lower rows: row one splits five columns three and two and drops its left card 80px, row two
 * is two halves. Every card keeps its own height (`items-start`): the cards have a visible surface,
 * and stretched to their neighbour's height they would end on a blank band. Below `lg` every card
 * takes the full width, in reading order.
 *
 * The one-column base is `minmax(0,1fr)` rather than `grid-cols-1`: the library's compiled CSS
 * loads after the app's and its own `grid-cols-1` would override the `lg:` template, and a track
 * with a zero minimum keeps long content from widening the column.
 */
const LOWER_ROW =
    'grid grid-cols-[minmax(0,1fr)] items-start gap-(--gap-gap-2xl) lg:gap-(--gap-gap-4xl)';

export const AssessmentTab = ({
    scoreData,
    employeeId,
    onEdit,
    canStartEvaluations,
    canAssignCompetencyProfiles,
    pickedPeriodId,
    onPickPeriod,
    onViewAllObjectives,
    showsObjectives,
    skillsetFilter,
    onSkillsetFilterChange,
    scorePeriodLabel,
    isScoreStale = false,
}: AssessmentTabProps) => {
    const { data: employee } = useGetUserById({ userId: employeeId });
    const isEmployeeInactive = employee && !employee.isActive;
    const { t } = useTranslation();

    return (
        <div
            className={'flex flex-col gap-8 md:!gap-[64px]'}
            data-testid={'assessment-tab'}
        >
            <div className={'ds-gap-4xl flex flex-col sm:flex-row'}>
                {/* Left: Performance Score card. It wears the same surface as the trend card beside
                    it -- same radius, same glass, declared once in `headlineCard` -- and the
                    reference's own 402px, which falls between the `w-100` and `w-101` steps. */}
                <Card
                    borderRadius={'3xl'}
                    bgColor={'bg-white/60'}
                    className={`${HEADLINE_CARD_SURFACE} sm:!w-[402px]`}
                >
                    <div
                        aria-busy={isScoreStale}
                        data-testid={'performance-score-card'}
                        className={`transition-opacity ${isScoreStale ? 'opacity-60' : ''}`}
                    >
                        <h3 className={HEADLINE_CARD_TITLE}>
                            {t('performanceProfile.performanceScore.title')}
                        </h3>
                        <PerformanceScoreDial
                            score={scoreData}
                            periodLabel={scorePeriodLabel}
                            dataTestId={'performance-score'}
                        />
                    </div>
                </Card>
                <PerformanceTrendChart
                    employeeId={employeeId}
                    className={'min-w-0 flex-1'}
                />
            </div>
            <CompetencyProfileCard
                employeeId={employeeId}
                onEditCurrentPosition={onEdit}
                isEmployeeInactive={isEmployeeInactive}
                canAssignCompetencyProfiles={canAssignCompetencyProfiles}
            />
            {/* Skill Assesment Section */}
            <div className={'relative z-10'}>
                <SkillsetAssessmentCard
                    employeeId={employeeId}
                    evaluatorId={skillsetFilter.evaluatorId}
                    onEvaluatorChange={(evaluatorId) =>
                        onSkillsetFilterChange({ ...skillsetFilter, evaluatorId })
                    }
                    period={skillsetFilter.period}
                    onPeriodChange={(period) =>
                        onSkillsetFilterChange({ ...skillsetFilter, period })
                    }
                />
            </div>
            <div className={showsObjectives ? `${LOWER_ROW} lg:grid-cols-5` : LOWER_ROW}>
                {showsObjectives && (
                    <ObjectivesAchievementCard
                        employeeId={employeeId}
                        employeeFirstName={employee?.firstName}
                        pickedPeriodId={pickedPeriodId}
                        onPickPeriod={onPickPeriod}
                        onViewAllObjectives={onViewAllObjectives}
                        className={'lg:col-span-3 lg:mt-[80px]'}
                    />
                )}
                <ManagerAssessmentPanel
                    employeeId={employeeId}
                    isEmployeeInactive={isEmployeeInactive}
                    canStartEvaluations={canStartEvaluations}
                    className={showsObjectives ? 'lg:col-span-2' : undefined}
                />
            </div>
            <div className={`${LOWER_ROW} lg:grid-cols-2`}>
                <SelfEvaluationPanel
                    employeeId={employeeId}
                    isEmployeeInactive={isEmployeeInactive}
                    canStartEvaluations={canStartEvaluations}
                />
                <Evaluation360Panel
                    employeeId={employeeId}
                    isEmployeeInactive={isEmployeeInactive}
                    canStartEvaluations={canStartEvaluations}
                />
            </div>
        </div>
    );
};
